import type { BaseSyntheticEvent } from 'react';
import { useCallback, useContext, useState } from 'react';
import type { FormState, SubmitHandler, UseFormRegister } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useErrorBoundary } from 'react-error-boundary';
import { getCurrentTimestampMs } from '../../../utils/time';
import { createEmailInputResolver, type EmailInputType } from '../utils/emailInputSchema';
import { putAccount } from '#/api/apiClient';
import { allowDomains, disallowDomains } from '#/utils/env';
import { SnackbarContext } from '#/utils/snackbar/snackbarProvider';

export type InputsType = EmailInputType;

export type UseChangeFormProps = {
  isSuccess: boolean;
  email: string;
  register: UseFormRegister<InputsType>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  formState: FormState<InputsType>;
};

export function useChangeForm(): UseChangeFormProps {
  const { showBoundary } = useErrorBoundary();
  const snackbar = useContext(SnackbarContext);
  const { mutateAsync } = useMutation({
    mutationFn: (email: string) => putAccount({ email }),
  });
  const { register, handleSubmit, formState } = useForm<InputsType>({
    resolver: createEmailInputResolver({ allowDomains, disallowDomains }),
    mode: 'onBlur',
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const onSubmit: SubmitHandler<InputsType, Promise<void>> = useCallback(
    async (data) => {
      const result = await mutateAsync(data.email);
      if (result.response.ok) {
        setEmail(data.email);
        setIsSuccess(true);
        return;
      }
      if (result.response.status === 400) {
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '入力されたメールアドレスは使用できません。別のメールアドレスを入力してください。',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      if (result.response.status === 409) {
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '保護者のメールアドレスとして登録されているものはサブメールアドレスに使用できません。',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      showBoundary(result.error);
    },
    [mutateAsync, showBoundary, snackbar]
  );
  return {
    register,
    isSuccess,
    email,
    onSubmit: handleSubmit(onSubmit),
    formState,
  };
}
